const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getFirstNonEmptyStringPropertyFromValue = (
  value: unknown,
  propertyName: string,
): string | undefined => {
  const seen = new WeakSet<object>();

  const findProp = (current: unknown): string | undefined => {
    if (!current || typeof current !== 'object') return undefined;
    if (seen.has(current)) return undefined;
    seen.add(current);

    if (Array.isArray(current)) {
      return current.reduce<string | undefined>(
        (found, item) => found ?? findProp(item),
        undefined,
      );
    }

    if (!isRecord(current)) return undefined;

    const directValue = current[propertyName];
    if (typeof directValue === 'string' && directValue.length > 0) {
      return directValue;
    }

    return Object.values(current).reduce<string | undefined>(
      (found, child) => found ?? findProp(child),
      undefined,
    );
  };

  return findProp(value);
};

/**
 * This helper recursively searches the provided value for a non-empty string `url`, since asset entities may expose description at different paths depending on asset type/schema.
 */
export const getAssetUrlFromDomainEntityAttributes = (
  domainEntityAttributes: unknown,
): string | undefined =>
  getFirstNonEmptyStringPropertyFromValue(domainEntityAttributes, 'url');

/**
 * This helper recursively searches the provided value for a non-empty string `title`, since asset entities may expose description at different paths depending on asset type/schema.
 */
export const getAssetTitleFromDomainEntityAttributes = (
  domainEntityAttributes: unknown,
): string | undefined =>
  getFirstNonEmptyStringPropertyFromValue(domainEntityAttributes, 'title');

/**
 * This helper recursively searches the provided value for a non-empty string `description`, since asset entities may expose description at different paths depending on asset type/schema.
 */
export const getAssetDescriptionFromDomainEntityAttributes = (
  domainEntityAttributes: unknown,
): string | undefined =>
  getFirstNonEmptyStringPropertyFromValue(
    domainEntityAttributes,
    'description',
  );
